import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertCircle, Users,
  Stethoscope, Package, ArrowRight, Globe, Printer, Download, Search, FileSpreadsheet, Share2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { exportToCSV, exportToExcel, formatDateForExport } from '@/utils/exportUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNutritionAnalytics, useGeoDistribution, useNutritionCountries } from '@/hooks/useNutritionAdmin';
import { useTranslation } from 'react-i18next';
import GenericShareModal from '@/components/GenericShareModal';

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(210, 70%, 50%)',
  'hsl(150, 60%, 45%)',
  'hsl(280, 60%, 55%)',
  'hsl(30, 80%, 55%)',
  'hsl(0, 70%, 55%)',
  'hsl(180, 50%, 45%)',
  'hsl(60, 70%, 45%)',
  'hsl(330, 60%, 55%)',
];

const NutritionB2BAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const { data: countries } = useNutritionCountries();
  const { data: analytics, isLoading, error } = useNutritionAnalytics(selectedCountry);
  const { data: geoData } = useGeoDistribution();
  const printRef = useRef<HTMLDivElement>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handlePrint = () => {
    setShowAllBrands(true);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const shareTitle = t('nutritionAdmin.analytics.reportTitle');
  const shareText = `${shareTitle}\n${t('nutritionAdmin.analytics.generatedAt')}: ${new Date().toLocaleString()}`;
  const shareUrl = window.location.href;

  // Get unique brands from market share data
  const availableBrands = analytics?.market_share?.map(item => item.brand).filter(Boolean) || [];

  // Export users for a specific brand
  const handleExportBrandUsers = async (brandName: string, format: 'csv' | 'excel') => {
    setIsExporting(true);
    try {
      // Query pets with this brand from pet_nutrition
      let query = supabase
        .from('pet_nutrition')
        .select(`
          id,
          brand,
          food_type,
          product_name,
          feeding_frequency,
          portion_size,
          diet_start_date,
          notes,
          pets!inner(
            id,
            name,
            species,
            breed,
            user_id
          )
        `)
        .eq('is_current', true)
        .ilike('brand', `%${brandName}%`);

      const { data: nutritionData, error: nutritionError } = await query;

      if (nutritionError) throw nutritionError;

      if (!nutritionData || nutritionData.length === 0) {
        toast.error(t('nutritionAdmin.analytics.noDataForBrand'));
        return;
      }

      // Get user info for each pet
      const userIds = [...new Set(nutritionData.map(n => (n.pets as any)?.user_id).filter(Boolean))];

      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, full_name, subscription_tier')
        .in('id', userIds);

      if (usersError) throw usersError;

      const userMap = new Map((usersData || []).map(u => [u.id, u]));

      // Get profile info for location
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, city, state, country')
        .in('user_id', userIds);

      const profileMap = new Map((profilesData || []).map(p => [p.user_id, p]));

      // Build export data
      const exportData = nutritionData.map(n => {
        const pet = n.pets as any;
        const user = userMap.get(pet?.user_id);
        const profile = profileMap.get(pet?.user_id);

        return {
          user_name: user?.full_name || '',
          user_email: user?.email || '',
          subscription_tier: user?.subscription_tier || 'free',
          pet_name: pet?.name || '',
          species: pet?.species || '',
          breed: pet?.breed || '',
          brand: n.brand || '',
          product_name: n.product_name || '',
          food_type: n.food_type || '',
          feeding_frequency: n.feeding_frequency || '',
          portion_size: n.portion_size || '',
          diet_start_date: n.diet_start_date || '',
          city: profile?.city || '',
          state: profile?.state || '',
          country: profile?.country || '',
        };
      });

      const columns = [
        { key: 'user_name', header: t('nutritionAdmin.export.userName') },
        { key: 'user_email', header: t('nutritionAdmin.export.userEmail') },
        { key: 'subscription_tier', header: t('nutritionAdmin.export.subscriptionPlan') },
        { key: 'pet_name', header: t('nutritionAdmin.export.petName') },
        { key: 'species', header: t('nutritionAdmin.export.species') },
        { key: 'breed', header: t('nutritionAdmin.export.breed') },
        { key: 'brand', header: t('nutritionAdmin.export.brand') },
        { key: 'product_name', header: t('nutritionAdmin.export.productName') },
        { key: 'food_type', header: t('nutritionAdmin.export.foodType') },
        { key: 'feeding_frequency', header: t('nutritionAdmin.export.feedingFrequency') },
        { key: 'portion_size', header: t('nutritionAdmin.export.portionSize') },
        { key: 'diet_start_date', header: t('nutritionAdmin.export.dietStartDate') },
        { key: 'city', header: t('nutritionAdmin.export.city') },
        { key: 'state', header: t('nutritionAdmin.export.state') },
        { key: 'country', header: t('nutritionAdmin.export.country') },
      ];

      const filename = `nutrition_brand_${brandName.replace(/\s+/g, '_')}`;
      if (format === 'excel') {
        await exportToExcel(exportData, columns, filename, t('nutritionAdmin.analytics.brandData'));
      } else {
        exportToCSV(exportData, columns, filename);
      }
      toast.success(t('nutritionAdmin.analytics.exportSuccess', { count: exportData.length }));

    } catch (err) {
      console.error('Export error:', err);
      toast.error(t('nutritionAdmin.analytics.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="h-[300px] flex items-center justify-center">
            <Skeleton className="h-full w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive">{t('nutritionAdmin.analytics.loadError')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  const marketShareData = analytics.market_share.map((item, index) => ({
    name: item.brand || t('nutritionAdmin.analytics.noBrand'),
    value: item.pet_count,
    percentage: item.market_share_percentage,
    fill: COLORS[index % COLORS.length],
  }));

  const switchingData = analytics.brand_switching.slice(0, 10);

  return (
    <div className="space-y-6 pb-24 print:p-4" ref={printRef} id="nutrition-b2b-print">
      {/* Print and Share Buttons */}
      <div className="flex justify-end gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={handleShare}>
          <Share2 className="h-4 w-4 mr-2" />
          {t('nutritionAdmin.analytics.share')}
        </Button>
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('nutritionAdmin.analytics.print')}
        </Button>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print:mb-6">
        <h1 className="text-2xl font-bold">{t('nutritionAdmin.analytics.reportTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('nutritionAdmin.analytics.generatedAt')}: {new Date().toLocaleString()}
          {selectedCountry && ` | ${t('nutritionAdmin.analytics.country')}: ${selectedCountry}`}
        </p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
        {/* Top Brand Card */}
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('nutritionAdmin.analytics.topBrand')}</CardTitle>
            <Package className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.top_brand.brand || 'N/A'}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {analytics.top_brand.growth_percentage !== undefined && (
                <>
                  {analytics.top_brand.growth_percentage >= 0 ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-sm ${analytics.top_brand.growth_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analytics.top_brand.growth_percentage >= 0 ? '+' : ''}
                    {analytics.top_brand.growth_percentage}%
                  </span>
                </>
              )}
              <span className="text-xs text-muted-foreground">
                ({t('nutritionAdmin.analytics.petsCount', { count: analytics.top_brand.current_count || 0 })})
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Users Without Provider */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('nutritionAdmin.analytics.usersWithoutProvider')}</CardTitle>
            <Users className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {analytics.pets_without_provider}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('nutritionAdmin.analytics.assignmentOpportunity')}
            </p>
            {analytics.pets_without_provider > 0 && (
              <Badge variant="outline" className="mt-2 text-amber-600 border-amber-500">
                ⚠️ {t('nutritionAdmin.analytics.requiresAttention')}
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Prescription Diets */}
        <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('nutritionAdmin.analytics.prescriptionDiets')}</CardTitle>
            <Stethoscope className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {analytics.prescription_diets}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('nutritionAdmin.analytics.requireVetProvider')}
            </p>
            {analytics.prescription_diets > 0 && (
              <Badge variant="outline" className="mt-2 text-red-600 border-red-500">
                🏥 {t('nutritionAdmin.analytics.medicalPriority')}
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Market Share Chart */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t('nutritionAdmin.analytics.marketShare')}
              </CardTitle>
              <CardDescription>
                {t('nutritionAdmin.analytics.brandDistribution', { count: analytics.total_diets })}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={selectedCountry || 'all'}
                onValueChange={(v) => setSelectedCountry(v === 'all' ? null : v)}
              >
                <SelectTrigger className="w-[180px]">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('nutritionAdmin.analytics.allCountries')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('nutritionAdmin.analytics.allCountries')}</SelectItem>
                  {countries?.map((c) => (
                    <SelectItem key={c.country} value={c.country}>
                      {c.country} ({c.pet_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Brand Export Section */}
          <div className="mt-4 p-4 bg-muted/30 rounded-lg border print:hidden">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('nutritionAdmin.analytics.exportBrandReport')}</span>
              </div>
              <div className="flex flex-1 items-center gap-2">
                <Select
                  value={selectedBrand || ''}
                  onValueChange={(v) => setSelectedBrand(v || null)}
                >
                  <SelectTrigger className="flex-1 max-w-[300px]">
                    <SelectValue placeholder={t('nutritionAdmin.analytics.selectBrand')} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBrands.map((brand) => (
                      <SelectItem key={brand} value={brand || ''}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="default"
                  size="sm"
                  disabled={!selectedBrand || isExporting}
                  onClick={() => selectedBrand && handleExportBrandUsers(selectedBrand, 'csv')}
                >
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!selectedBrand || isExporting}
                  onClick={() => selectedBrand && handleExportBrandUsers(selectedBrand, 'excel')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Excel
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('nutritionAdmin.analytics.exportBrandDescription')}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
            {/* Pie Chart - With labels for print and hover tooltip for screen */}
            <div className="h-[350px] sm:h-[400px] print:h-[300px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={marketShareData.slice(0, 10)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, index, name, percentage }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      // Truncate long names for display
                      const displayName = name.length > 12 ? name.substring(0, 10) + '...' : name;
                      
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="hsl(var(--foreground))"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          className="text-[9px] sm:text-[10px] md:text-xs print:text-[8px] font-medium"
                          style={{ 
                            fontSize: 'clamp(8px, 2vw, 12px)',
                            fontWeight: 500
                          }}
                        >
                          {displayName} ({percentage}%)
                        </text>
                      );
                    }}
                    labelLine={{
                      stroke: 'hsl(var(--muted-foreground))',
                      strokeWidth: 1,
                    }}
                  >
                    {marketShareData.slice(0, 10).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-sm">{data.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.value} {t('nutritionAdmin.analytics.pets')} ({data.percentage}%)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Brand Legend List - Always visible, acts as a legend */}
            <div className={`space-y-2 ${showAllBrands ? '' : 'max-h-[350px] sm:max-h-[400px] overflow-y-auto'} print:max-h-none print:overflow-visible`}>
              <div className="text-sm font-semibold text-muted-foreground mb-2 print:text-xs">
                {t('nutritionAdmin.analytics.brandLegend', 'Brand Legend')}
              </div>
              {marketShareData.map((item, index) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors print:p-1.5 print:border-b print:border-border/50"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0 print:w-3 print:h-3"
                      style={{ backgroundColor: item.fill }}
                    />
                    <span className="font-medium text-sm truncate print:text-xs">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="secondary" className="text-xs print:text-[10px]">{item.value}</Badge>
                    <span className="text-xs text-muted-foreground print:text-[10px] min-w-[40px] text-right">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Show all toggle for screen */}
          {marketShareData.length > 10 && (
            <div className="mt-4 text-center print:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllBrands(!showAllBrands)}
              >
                {showAllBrands
                  ? t('nutritionAdmin.analytics.showLess')
                  : t('nutritionAdmin.analytics.showAllBrands', { count: marketShareData.length })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Brand Switching Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="h-5 w-5" />
            {t('nutritionAdmin.analytics.brandSwitching')}
          </CardTitle>
          <CardDescription>
            {t('nutritionAdmin.analytics.brandSwitchingDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {switchingData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('nutritionAdmin.analytics.noSwitchingData')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {switchingData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Badge variant="outline" className="text-red-600 border-red-300">
                      {item.from_brand || t('nutritionAdmin.analytics.noBrand')}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      {item.to_brand || t('nutritionAdmin.analytics.noBrand')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">{item.switch_count}</span>
                    <span className="text-sm text-muted-foreground">{t('nutritionAdmin.analytics.switches')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Geographic Distribution */}
      {geoData && geoData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('nutritionAdmin.analytics.geoDistribution')}</CardTitle>
            <CardDescription>
              {t('nutritionAdmin.analytics.geoDistributionDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={geoData.slice(0, 10)}
                  layout="vertical"
                  margin={{ left: 100 }}
                >
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="city" width={90} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="premium_count" name={t('nutritionAdmin.analytics.premium')} fill="hsl(var(--primary))" stackId="stack" />
                  <Bar dataKey="economy_count" name={t('nutritionAdmin.analytics.economy')} fill="hsl(var(--secondary))" stackId="stack" />
                  <Bar dataKey="prescription_count" name={t('nutritionAdmin.analytics.prescription')} fill="hsl(0, 70%, 55%)" stackId="stack" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #nutrition-b2b-print, #nutrition-b2b-print * {
            visibility: visible;
          }
          #nutrition-b2b-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .print\\:grid-cols-3 {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
          .print\\:max-h-none {
            max-height: none !important;
          }
          .print\\:overflow-visible {
            overflow: visible !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
          .print\\:p-4 {
            padding: 1rem;
          }
          .print\\:text-sm {
            font-size: 0.875rem;
          }
          .print\\:text-xs {
            font-size: 0.75rem;
          }
          /* Ensure pie chart labels are visible and don't overlap */
          .recharts-pie-label-text {
            font-size: 8px !important;
            fill: #000 !important;
          }
          .recharts-pie-label-line {
            stroke: #666 !important;
          }
          /* Ensure SVG elements print correctly */
          svg {
            overflow: visible !important;
          }
          .recharts-wrapper {
            overflow: visible !important;
          }
          .recharts-surface {
            overflow: visible !important;
          }
        }
      `}</style>

      {/* Share Modal */}
      <GenericShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={shareTitle}
        description={t('nutritionAdmin.analytics.shareDescription', 'Share the nutrition analytics report')}
        shareText={shareText}
        shareUrl={shareUrl}
      />
    </div>
  );
};

export default NutritionB2BAnalytics;
